import { useEffect, useRef } from 'react';
import * as Style from './AutoSwiper.style';

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  height: number;
  animation: {
    /**
     * 1초에 이동하는 픽셀 수
     *
     * 애니메이션 속도를 결정하는 기준값입니다.
     */
    pixelPerSecond?: number,
    /**
     * seconds
     */
    delay?: number,
    direction?: 'left' | 'right',
  };
  itemGap?: number;
  children: React.ReactNode;
}

/**
 * @see https://web.dev/articles/prefers-reduced-motion?hl=ko
 */
const READERS_ENABLED_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

const PIXEL_PER_SECOND = 96;

const AutoSwiper = ({
  height,
  animation,
  itemGap = 100,
  children,
  ...rest
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const scrollerInnerRef = useRef<HTMLUListElement>(null);

  useEffect(function initializeAnimation() {
    if (!scrollerRef.current) return;
    if (!scrollerInnerRef.current) return;

    if (window.matchMedia(READERS_ENABLED_REDUCED_MOTION).matches) return;

    const scroller = scrollerRef.current;
    const scrollerInner = scrollerInnerRef.current;
    if (scrollerInner.getAttribute('data-copied')) return;

    scroller.setAttribute('data-animated', 'true');
    setScrollerInnerAnimationDuration();
    duplicateChildren(scrollerInner, animation.direction);
    (animation.direction === 'right') && alignScrollerInnerToEnd();

    function alignScrollerInnerToEnd() {
      const length = scrollerInner.getBoundingClientRect().width;
      const offsetX = window.innerWidth - length - (itemGap / 2);
      scrollerInner.style.position = 'relative';
      scrollerInner.style.left = `${offsetX}px`;
    }

    function setScrollerInnerAnimationDuration() {
      const length = scrollerInner.getBoundingClientRect().width;
      const animationDuration = length / (animation.pixelPerSecond ?? PIXEL_PER_SECOND);
      scrollerInner.style.animationDuration = `${animationDuration}s`;
    }
  }, [animation.direction, animation.pixelPerSecond, itemGap]);

  useEffect(function handleReducedMotionChange() {
    const mediaQuery = window.matchMedia(READERS_ENABLED_REDUCED_MOTION);

    const handler = () => {
      if (mediaQuery.matches) {
        scrollerRef.current?.setAttribute('data-animated', 'false');
      } else {
        scrollerRef.current?.setAttribute('data-animated', 'true');
      }
    };

    mediaQuery.addEventListener('change', handler);

    return () => {
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  return (
    <Style.Scroller
      ref={scrollerRef}
      itemGap={itemGap}
      animation={animation}
      {...rest}
    >
      <Style.ScrollerInner
        id="scroller-inner"
        ref={scrollerInnerRef}
        height={height}
        itemGap={itemGap}
        direction={animation.direction}
      >
        {children}
      </Style.ScrollerInner>
    </Style.Scroller>
  );
};

export default AutoSwiper;

const duplicateChildren = (element: HTMLElement, direction?: 'right' | 'left' | undefined) => {
  const children = Array.from(element.children);

  children.forEach((child) => {
    const duplicatedItem = child.cloneNode(true) as HTMLElement;
    element.appendChild(duplicatedItem);

    if (direction === 'right') {
      child.setAttribute('aria-hidden', 'true');
    } else {
      duplicatedItem.setAttribute('aria-hidden', 'true');
    }
  });

  element.setAttribute('data-copied', 'true');
};
